import dayjs from 'dayjs'
import dayjsBusinessDays from 'dayjs-business-days'

dayjs.extend(dayjsBusinessDays)

const COUNTRY_KEY = 'storeActiveCountryStatus'
const SHOPIFY_COUNTRY_KEY = 'storeShopifyCountry'

/**
 * Resolves which delivery region to use.
 *
 * `defaultCountry` comes from the storefront country picked in the footer
 * (Shopify localization). When it changes, it overrides any stale manual
 * selection so the message follows the footer country. Otherwise the last
 * manual choice wins.
 */
export const resolveDeliveryCountry = deliveryOpt => {
  const shopifyCountry = deliveryOpt.defaultCountry
  const storedShopifyCountry = localStorage.getItem(SHOPIFY_COUNTRY_KEY)
  const storedActiveCountry = localStorage.getItem(COUNTRY_KEY)

  if (storedShopifyCountry !== shopifyCountry) {
    localStorage.setItem(SHOPIFY_COUNTRY_KEY, shopifyCountry)
    localStorage.setItem(COUNTRY_KEY, shopifyCountry)
    return shopifyCountry
  }

  return storedActiveCountry || shopifyCountry
}

export const setDeliveryCountry = country => {
  localStorage.setItem(COUNTRY_KEY, country)
}

/**
 * Number of extra business days added when the order is placed after the
 * daily cut-off — it can no longer go out today.
 */
const cutOffDaysAdded = deliveryOpt => {
  const currentTime = new Date()
  const changeDayTimeStr = currentTime.toISOString().split('T')[0] + 'T' + deliveryOpt.cutofftime
  const differenceMs = new Date(changeDayTimeStr) - currentTime

  return differenceMs > 0 ? 0 : 1
}

const shippingDaysFor = (deliveryOpt, country) => {
  if (country === 'UK') return deliveryOpt.uk
  if (country === 'US') return deliveryOpt.us
  if (country === 'EU') return deliveryOpt.eu
  return deliveryOpt.row
}

/**
 * Stock bucket for a variant. Mirrors the PDP wording:
 * `inStock` / `lowStock` / `madeOrder` / `Outofstock`.
 *
 * @param {object} stock - { inventory, policy, management }
 * @returns {object} { stockValue, deliveryMessageStatus }
 */
export const resolveStockValue = stock => {
  const inventory = parseInt(stock.inventory, 10) || 0

  if ((inventory <= 0 && stock.policy === 'continue') || stock.management === '') {
    return { stockValue: 'madeOrder', deliveryMessageStatus: true }
  }

  if (inventory > 0) {
    return {
      stockValue: inventory < 3 ? 'lowStock' : 'inStock',
      deliveryMessageStatus: true
    }
  }

  return { stockValue: 'Outofstock', deliveryMessageStatus: true }
}

/**
 * Estimated delivery text for a variant, e.g. `UK by August 18`.
 *
 * Out-of-stock items that are still orderable (`policy === 'continue'`) add
 * the product's manufacture time on top of the shipping time.
 *
 * @param {object} deliveryOpt - serialized `json-delivery` payload
 * @param {object} stock - { inventory, policy, management }
 * @param {string} country - `UK` | `US` | `EU` | `REST OF WORLD`
 */
export const buildDeliveryHtml = (deliveryOpt, stock, country) => {
  const shippingTime = shippingDaysFor(deliveryOpt, country)
  const cutOff = cutOffDaysAdded(deliveryOpt)
  const inventory = parseInt(stock.inventory, 10) || 0

  let days = parseInt(shippingTime, 10) + cutOff

  if (inventory < 1 && stock.policy === 'continue') {
    days = parseInt(shippingTime, 10) + parseInt(deliveryOpt.manufactureTime, 10) + cutOff
  }

  return country + ' by ' + dayjs().businessDaysAdd(days).format('MMMM DD')
}

/**
 * Everything needed to render one delivery line.
 *
 * @returns {object} { stockValue, deliveryMessageStatus, deliveryHtml }
 */
export const buildDeliveryMessage = (deliveryOpt, stock, country) => {
  const { stockValue, deliveryMessageStatus } = resolveStockValue(stock)

  return {
    stockValue,
    deliveryMessageStatus,
    deliveryHtml: buildDeliveryHtml(deliveryOpt, stock, country)
  }
}
